// AdminEventManagement.tsx
import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import {
  createEvent,
  deleteEvent,
  fetchEmployeeByEmail,
  fetchEmployeeDirectory,
  fetchEventParticipantCounts,
  fetchEventParticipants,
  fetchEvents,
  fetchManagerViewerByAuthUserId,
  getCurrentUser,
  removeEventParticipant,
  updateEvent,
  upsertEmployeeAssessmentOccasion,
} from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import type { EventParticipant, EventRow } from "../types/events";

function SectionIconBox(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2.5,
        bgcolor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        display: "grid",
        placeItems: "center",
        color: "#111827",
        flexShrink: 0,
      }}
    >
      {props.children}
    </Box>
  );
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "event";
}

function toKeyword(name: string, eventDate: string | null) {
  const slug = slugify(name);
  if (!eventDate) return slug;
  const compact = eventDate.replace(/-/g, "");
  return `${slug}_${compact}`;
}

export default function AdminEventManagement(props: { handlePageChange: (page: string) => void }) {
  void props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback((en: string, de: string) => (typedLang === "de" ? de : en), [typedLang]);

  const [loading, setLoading] = React.useState(true);
  const [uiError, setUiError] = React.useState<string | null>(null);
  const [events, setEvents] = React.useState<EventRow[]>([]);
  const [participantCounts, setParticipantCounts] = React.useState<Record<string, number>>({});

  const [addOpen, setAddOpen] = React.useState(false);
  const [addName, setAddName] = React.useState("");
  const [addDate, setAddDate] = React.useState("");
  const [addKeyword, setAddKeyword] = React.useState("");
  const [addKeywordEdited, setAddKeywordEdited] = React.useState(false);
  const [addSaving, setAddSaving] = React.useState(false);
  const [addError, setAddError] = React.useState<string | null>(null);

  const [editTarget, setEditTarget] = React.useState<EventRow | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editDate, setEditDate] = React.useState("");
  const [editKeyword, setEditKeyword] = React.useState("");
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = React.useState<EventRow | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const [participantsTarget, setParticipantsTarget] = React.useState<EventRow | null>(null);
  const [participants, setParticipants] = React.useState<EventParticipant[]>([]);
  const [participantsLoading, setParticipantsLoading] = React.useState(false);
  const [participantsError, setParticipantsError] = React.useState<string | null>(null);
  const [employeeDirectory, setEmployeeDirectory] = React.useState<EventParticipant[]>([]);
  const [addEmail, setAddEmail] = React.useState("");
  const [addSelectedEmployee, setAddSelectedEmployee] = React.useState<EventParticipant | null>(null);
  const [addingParticipant, setAddingParticipant] = React.useState(false);
  const [addParticipantError, setAddParticipantError] = React.useState<string | null>(null);
  const [removingEmployeeId, setRemovingEmployeeId] = React.useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<EventParticipant | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setUiError(null);

    try {
      const { data: userData, error: userErr } = await getCurrentUser();
      if (userErr) throw userErr;
      if (!userData?.user) {
        throw new Error(L("Not authenticated.", "Nicht angemeldet."));
      }

      const { data: me, error: meErr } = await fetchManagerViewerByAuthUserId(userData.user.id);
      if (meErr) throw meErr;
      if (!me?.id || String(me.company_role) !== "admin") {
        throw new Error(L("You do not have admin access.", "Du hast keinen Admin-Zugriff."));
      }

      const [
        { data: eventRows, error: eventsErr },
        { data: counts, error: countsErr },
        { data: directoryRows, error: directoryErr },
      ] = await Promise.all([fetchEvents(), fetchEventParticipantCounts(), fetchEmployeeDirectory()]);

      if (eventsErr) throw eventsErr;
      if (countsErr) throw countsErr;
      if (directoryErr) throw directoryErr;

      setEvents((eventRows ?? []) as EventRow[]);
      setParticipantCounts(counts ?? {});
      setEmployeeDirectory((directoryRows ?? []) as EventParticipant[]);
    } catch (err) {
      setUiError(
        err instanceof Error ? err.message : L("Something went wrong.", "Etwas ist schiefgelaufen.")
      );
    } finally {
      setLoading(false);
    }
  }, [L]);

  React.useEffect(() => {
    load();
  }, [load]);

  function openAddDialog() {
    setAddName("");
    setAddDate("");
    setAddKeyword("");
    setAddKeywordEdited(false);
    setAddError(null);
    setAddOpen(true);
  }

  function handleAddNameOrDateChange(nextName: string, nextDate: string) {
    setAddName(nextName);
    setAddDate(nextDate);
    if (!addKeywordEdited) {
      setAddKeyword(toKeyword(nextName, nextDate || null));
    }
  }

  function closeAddDialog() {
    if (addSaving) return;
    setAddOpen(false);
  }

  async function handleCreateEvent() {
    const name = addName.trim();
    if (!name) return;

    setAddSaving(true);
    setAddError(null);

    const eventDate = addDate || null;
    const keyword = addKeyword.trim() || toKeyword(name, eventDate);

    const { data, error } = await createEvent(name, eventDate, keyword);

    if (error || !data) {
      setAddError(L("Failed to create event.", "Anlass konnte nicht erstellt werden."));
      setAddSaving(false);
      return;
    }

    setEvents((prev) =>
      [...prev, data as EventRow].sort((a, b) => (b.event_date ?? "").localeCompare(a.event_date ?? ""))
    );
    setAddSaving(false);
    setAddOpen(false);
  }

  function openEditDialog(event: EventRow) {
    setEditTarget(event);
    setEditName(event.name);
    setEditDate(event.event_date ?? "");
    setEditKeyword(event.occasion_keyword);
    setEditError(null);
  }

  function closeEditDialog() {
    if (editSaving) return;
    setEditTarget(null);
  }

  async function handleSaveEdit() {
    if (!editTarget) return;
    const name = editName.trim();
    if (!name) return;
    const keyword = editKeyword.trim();
    if (!keyword) return;

    setEditSaving(true);
    setEditError(null);

    const eventDate = editDate || null;

    const { error } = await updateEvent(editTarget.id, {
      name,
      event_date: eventDate,
      occasion_keyword: keyword,
    });

    if (error) {
      setEditError(L("Failed to save changes.", "Änderungen konnten nicht gespeichert werden."));
      setEditSaving(false);
      return;
    }

    setEvents((prev) =>
      prev
        .map((e) =>
          e.id === editTarget.id ? { ...e, name, event_date: eventDate, occasion_keyword: keyword } : e
        )
        .sort((a, b) => (b.event_date ?? "").localeCompare(a.event_date ?? ""))
    );
    setEditSaving(false);
    setEditTarget(null);
  }

  function openDeleteDialog(event: EventRow) {
    setDeleteTarget(event);
    setDeleteError(null);
  }

  function closeDeleteDialog() {
    if (deleting) return;
    setDeleteTarget(null);
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;

    setDeleting(true);
    setDeleteError(null);

    const { error } = await deleteEvent(deleteTarget.id);

    if (error) {
      setDeleteError(L("Failed to delete event.", "Anlass konnte nicht gelöscht werden."));
      setDeleting(false);
      return;
    }

    setEvents((prev) => prev.filter((e) => e.id !== deleteTarget.id));
    setDeleting(false);
    setDeleteTarget(null);
  }

  async function loadParticipants(eventId: string) {
    setParticipantsLoading(true);
    setParticipantsError(null);

    const { data, error } = await fetchEventParticipants(eventId);

    if (error) {
      setParticipantsError(L("Failed to load participants.", "Teilnehmende konnten nicht geladen werden."));
      setParticipantsLoading(false);
      return;
    }

    const rows = (data ?? [])
      .map((row: any) => row.employees as EventParticipant | null)
      .filter((row): row is EventParticipant => Boolean(row))
      .sort((a, b) => (a.last_name ?? "").localeCompare(b.last_name ?? ""));

    setParticipants(rows);
    setParticipantsLoading(false);
  }

  function openParticipantsDialog(event: EventRow) {
    setParticipantsTarget(event);
    setParticipants([]);
    setParticipantsError(null);
    setAddEmail("");
    setAddSelectedEmployee(null);
    setAddParticipantError(null);
    loadParticipants(event.id);
  }

  function closeParticipantsDialog() {
    if (addingParticipant || removingEmployeeId) return;
    setParticipantsTarget(null);
  }

  async function handleAddParticipant() {
    if (!participantsTarget) return;
    const email = addEmail.trim();
    if (!email) return;

    setAddingParticipant(true);
    setAddParticipantError(null);

    let employee = addSelectedEmployee;

    if (!employee || employee.company_email.toLowerCase() !== email.toLowerCase()) {
      const { data, error: lookupErr } = await fetchEmployeeByEmail(email);

      if (lookupErr || !data) {
        setAddParticipantError(
          L("No employee found with that email.", "Kein Mitarbeitender mit dieser E-Mail gefunden.")
        );
        setAddingParticipant(false);
        return;
      }

      employee = data as EventParticipant;
    }

    if (participants.some((p) => p.id === employee!.id)) {
      setAddParticipantError(
        L("This employee is already a participant.", "Dieser Mitarbeitende nimmt bereits teil.")
      );
      setAddingParticipant(false);
      return;
    }

    const { error: upsertErr } = await upsertEmployeeAssessmentOccasion(employee.id, participantsTarget.id);

    if (upsertErr) {
      setAddParticipantError(L("Failed to add participant.", "Teilnehmender konnte nicht hinzugefügt werden."));
      setAddingParticipant(false);
      return;
    }

    setParticipants((prev) =>
      [...prev, employee as EventParticipant].sort((a, b) => (a.last_name ?? "").localeCompare(b.last_name ?? ""))
    );
    setParticipantCounts((prev) => ({
      ...prev,
      [participantsTarget.id]: (prev[participantsTarget.id] ?? 0) + 1,
    }));
    setAddEmail("");
    setAddSelectedEmployee(null);
    setAddingParticipant(false);
  }

  function openRemoveConfirm(participant: EventParticipant) {
    setRemoveTarget(participant);
  }

  function closeRemoveConfirm() {
    if (removingEmployeeId) return;
    setRemoveTarget(null);
  }

  async function handleConfirmRemoveParticipant() {
    if (!participantsTarget || !removeTarget) return;

    const employeeId = removeTarget.id;
    setRemovingEmployeeId(employeeId);
    setParticipantsError(null);

    const { error } = await removeEventParticipant(participantsTarget.id, employeeId);

    if (error) {
      setParticipantsError(L("Failed to remove participant.", "Teilnehmender konnte nicht entfernt werden."));
      setRemovingEmployeeId(null);
      return;
    }

    setParticipants((prev) => prev.filter((p) => p.id !== employeeId));
    setParticipantCounts((prev) => ({
      ...prev,
      [participantsTarget.id]: Math.max(0, (prev[participantsTarget.id] ?? 1) - 1),
    }));
    setRemovingEmployeeId(null);
    setRemoveTarget(null);
  }

  const availableEmployees = React.useMemo(
    () => employeeDirectory.filter((e) => !participants.some((p) => p.id === e.id)),
    [employeeDirectory, participants]
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SectionIconBox>
              <EventOutlinedIcon />
            </SectionIconBox>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {L("Events", "Anlässe")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {L(
                  "Add, rename or reschedule the events employees can pick when starting an assessment.",
                  "Anlässe hinzufügen, umbenennen oder verschieben, die Mitarbeitende beim Start einer Bewertung auswählen können."
                )}
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openAddDialog}
            sx={{ borderRadius: 999, fontWeight: 900, textTransform: "none", px: 2 }}
          >
            {L("Add event", "Anlass hinzufügen")}
          </Button>
        </Stack>

        {uiError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {uiError}
          </Alert>
        ) : null}

        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={96} />
          </Stack>
        ) : events.length === 0 ? (
          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", p: 3 }}>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              {L("No events yet.", "Noch keine Anlässe vorhanden.")}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {events.map((event) => {
              const count = participantCounts[event.id] ?? 0;
              const canDelete = count === 0;

              return (
                <Paper
                  key={event.id}
                  elevation={0}
                  sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", p: { xs: 2, md: 2.5 } }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 2.25,
                          bgcolor: "#F3F4F6",
                          display: "grid",
                          placeItems: "center",
                          color: "#374151",
                          flexShrink: 0,
                        }}
                      >
                        <EventOutlinedIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: "#111827" }} noWrap>
                          {event.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6B7280" }}>
                          {(event.event_date ?? L("No date set", "Kein Datum")) +
                            " · " +
                            event.occasion_keyword +
                            " · " +
                            count +
                            " " +
                            L("participants", "Teilnehmende")}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={L("Manage participants", "Teilnehmende verwalten")}>
                        <IconButton size="small" onClick={() => openParticipantsDialog(event)}>
                          <GroupOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={L("Edit event", "Anlass bearbeiten")}>
                        <IconButton size="small" onClick={() => openEditDialog(event)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          canDelete
                            ? L("Delete event", "Anlass löschen")
                            : L(
                                "This event still has participants",
                                "Diesem Anlass sind noch Teilnehmende zugeordnet"
                              )
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canDelete}
                            onClick={() => openDeleteDialog(event)}
                            sx={{ color: canDelete ? "#B91C1C" : undefined }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      <Dialog open={addOpen} onClose={closeAddDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>{L("Add event", "Anlass hinzufügen")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {addError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {addError}
              </Alert>
            ) : null}
            <TextField
              autoFocus
              fullWidth
              label={L("Event name", "Name des Anlasses")}
              value={addName}
              onChange={(e) => handleAddNameOrDateChange(e.target.value, addDate)}
            />
            <TextField
              fullWidth
              type="date"
              label={L("Date", "Datum")}
              InputLabelProps={{ shrink: true }}
              value={addDate}
              onChange={(e) => handleAddNameOrDateChange(addName, e.target.value)}
            />
            <TextField
              fullWidth
              label={L("Keyword / note", "Schlüsselwort / Notiz")}
              helperText={L(
                "Used internally to link questions and answers to this event. Auto-filled from the name and date, but you can set your own.",
                "Wird intern verwendet, um Fragen und Antworten diesem Anlass zuzuordnen. Wird automatisch aus Name und Datum befüllt, kann aber frei gesetzt werden."
              )}
              value={addKeyword}
              onChange={(e) => {
                setAddKeyword(e.target.value);
                setAddKeywordEdited(true);
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAddDialog} disabled={addSaving}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateEvent}
            disabled={addSaving || !addName.trim() || !addKeyword.trim()}
          >
            {addSaving ? <CircularProgress size={18} /> : L("Create", "Erstellen")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editTarget} onClose={closeEditDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>{L("Edit event", "Anlass bearbeiten")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {editError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {editError}
              </Alert>
            ) : null}
            <TextField
              autoFocus
              fullWidth
              label={L("Event name", "Name des Anlasses")}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <TextField
              fullWidth
              type="date"
              label={L("Date", "Datum")}
              InputLabelProps={{ shrink: true }}
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
            />
            <TextField
              fullWidth
              label={L("Keyword / note", "Schlüsselwort / Notiz")}
              helperText={L(
                "Used internally to link questions and answers to this event.",
                "Wird intern verwendet, um Fragen und Antworten diesem Anlass zuzuordnen."
              )}
              value={editKeyword}
              onChange={(e) => setEditKeyword(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeEditDialog} disabled={editSaving}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editSaving || !editName.trim() || !editKeyword.trim()}
          >
            {editSaving ? <CircularProgress size={18} /> : L("Save", "Speichern")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: "#B91C1C" }}>
          {L("Delete event?", "Anlass löschen?")}
        </DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
              {deleteError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ color: "#374151" }}>
            {L(
              `Are you sure you want to permanently delete ${deleteTarget?.name ?? ""}? This cannot be undone.`,
              `Möchtest du ${deleteTarget?.name ?? ""} wirklich endgültig löschen? Dies kann nicht rückgängig gemacht werden.`
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : L("Delete", "Löschen")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!participantsTarget} onClose={closeParticipantsDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>
          {L("Participants", "Teilnehmende")}
          {participantsTarget ? ` – ${participantsTarget.name}` : ""}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {participantsError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {participantsError}
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                fullWidth
                size="small"
                freeSolo
                options={availableEmployees}
                filterOptions={(options, state) => {
                  const query = state.inputValue.trim().toLowerCase();
                  if (!query) return options;
                  return options.filter((option) => {
                    const label = [option.first_name, option.last_name, option.company_email]
                      .filter(Boolean)
                      .join(" ")
                      .toLowerCase();
                    return label.includes(query);
                  });
                }}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.company_email)}
                isOptionEqualToValue={(option, value) =>
                  typeof option !== "string" && typeof value !== "string" && option.id === value.id
                }
                inputValue={addEmail}
                onInputChange={(_, value) => {
                  setAddEmail(value);
                  setAddSelectedEmployee(null);
                  if (addParticipantError) setAddParticipantError(null);
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== "string") {
                    setAddSelectedEmployee(value);
                    setAddEmail(value.company_email);
                  }
                }}
                renderOption={(optionProps, option) => (
                  <Box component="li" {...optionProps} key={option.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>
                        {[option.first_name, option.last_name].filter(Boolean).join(" ") || option.company_email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6B7280" }}>
                        {option.company_email}
                      </Typography>
                    </Box>
                  </Box>
                )}
                disabled={addingParticipant}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={L("Add by email", "Per E-Mail hinzufügen")}
                    error={Boolean(addParticipantError)}
                    helperText={addParticipantError ?? undefined}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddParticipant();
                    }}
                  />
                )}
              />
              <Button
                variant="contained"
                onClick={handleAddParticipant}
                disabled={addingParticipant || !addEmail.trim()}
                sx={{ minHeight: 40, whiteSpace: "nowrap" }}
              >
                {addingParticipant ? <CircularProgress size={18} /> : L("Add", "Hinzufügen")}
              </Button>
            </Stack>

            {participantsLoading ? (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={52} />
                <Skeleton variant="rounded" height={52} />
              </Stack>
            ) : participants.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#6B7280", py: 1 }}>
                {L("No participants yet.", "Noch keine Teilnehmenden.")}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {participants.map((participant) => (
                  <Stack
                    key={participant.id}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: 2.5,
                      bgcolor: "#FFFFFF",
                      p: 1.25,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, color: "#111827" }} noWrap>
                        {[participant.first_name, participant.last_name].filter(Boolean).join(" ") ||
                          participant.company_email}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6B7280" }} noWrap>
                        {participant.company_email}
                      </Typography>
                    </Box>

                    <Tooltip title={L("Remove from event", "Vom Anlass entfernen")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={removingEmployeeId === participant.id}
                          onClick={() => openRemoveConfirm(participant)}
                          sx={{ color: "#B91C1C" }}
                        >
                          {removingEmployeeId === participant.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PersonRemoveOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeParticipantsDialog} disabled={Boolean(addingParticipant || removingEmployeeId)}>
            {L("Close", "Schließen")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!removeTarget} onClose={closeRemoveConfirm} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900, color: "#B91C1C" }}>
          {L("Remove participant?", "Teilnehmenden entfernen?")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#374151" }}>
            {L(
              `Are you sure you want to remove ${
                [removeTarget?.first_name, removeTarget?.last_name].filter(Boolean).join(" ") ||
                removeTarget?.company_email ||
                ""
              } from ${participantsTarget?.name ?? "this event"}?`,
              `Möchtest du ${
                [removeTarget?.first_name, removeTarget?.last_name].filter(Boolean).join(" ") ||
                removeTarget?.company_email ||
                ""
              } wirklich von ${participantsTarget?.name ?? "diesem Anlass"} entfernen?`
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeRemoveConfirm} disabled={Boolean(removingEmployeeId)}>
            {L("Cancel", "Abbrechen")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRemoveParticipant}
            disabled={Boolean(removingEmployeeId)}
          >
            {removingEmployeeId ? (
              <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
            ) : (
              L("Remove", "Entfernen")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
